import { useEffect, useState } from "react";
import { clearAllWritingDrafts } from "@/lib/writing-draft-storage";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle } from "lucide-react";

/**
 * Diagnostic view rendered when RoleContext reports `no_role`.
 *
 * Instead of a generic "contact your admin" message, this dumps the actual
 * inputs that produced the no_role decision so the real cause is visible:
 *   - auth session (user id / email)
 *   - raw `get_my_context` RPC payload + error
 *   - raw `user_roles` rows for this user
 */
export function AccountSetupIncomplete() {
  const [diag, setDiag] = useState<{
    userId: string | null;
    email: string | null;
    ctx: unknown;
    ctxError: unknown;
    rolesRows: unknown;
    rolesError: unknown;
    sessionError: unknown;
  } | null>(null);

  useEffect(() => {
    (async () => {
      const { data: sess, error: sessionError } = await supabase.auth.getSession();
      const userId = sess?.session?.user?.id ?? null;
      const email = sess?.session?.user?.email ?? null;

      const { data: ctx, error: ctxError } = await supabase.rpc("get_my_context" as any);

      let rolesRows: unknown = null;
      let rolesError: unknown = null;
      if (userId) {
        const r = await supabase
          .from("user_roles" as any)
          .select("role, project_id, assigned_at")
          .eq("user_id", userId);
        rolesRows = r.data;
        rolesError = r.error;
      }

      setDiag({ userId, email, ctx, ctxError, rolesRows, rolesError, sessionError });
    })();
  }, []);

  const handleSignOut = async () => {
    localStorage.removeItem("lurneax_active_role");
    await clearAllWritingDrafts(); // explicit user sign-out only
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const fmt = (v: unknown) => {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  return (
    <div
      role="alert"
      className="min-h-screen w-full bg-background p-6 overflow-auto"
    >
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-primary" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-foreground">
            Role resolution returned no_role — diagnostic
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          RoleContext resolved to <code>no_role</code> for this session. Raw
          inputs are shown below so the actual cause is visible instead of a
          generic fallback.
        </p>

        {!diag ? (
          <p className="text-sm">Collecting diagnostics…</p>
        ) : (
          <div className="space-y-4 text-xs">
            <section>
              <div className="font-semibold mb-1">auth.getSession()</div>
              <pre className="bg-muted p-3 rounded overflow-auto">
{fmt({ userId: diag.userId, email: diag.email, sessionError: diag.sessionError })}
              </pre>
            </section>
            <section>
              <div className="font-semibold mb-1">rpc: get_my_context</div>
              <pre className="bg-muted p-3 rounded overflow-auto">
{fmt({ ctx: diag.ctx, ctxError: diag.ctxError })}
              </pre>
            </section>
            <section>
              <div className="font-semibold mb-1">
                select role, project_id, assigned_at from user_roles where user_id = auth.uid()
              </div>
              <pre className="bg-muted p-3 rounded overflow-auto">
{fmt({ rows: diag.rolesRows, error: diag.rolesError })}
              </pre>
            </section>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Retry
          </Button>
          <Button onClick={handleSignOut} variant="default">
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
